import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR, maskCPF, maskPhone, STATUS_LABELS, DOC_TYPE_LABELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatusBadge } from "../components/Badges";
import { toast } from "sonner";
import { Plus, Search, Filter, Loader2, X, ArrowRight, Users as UsersIcon, Upload, FileCheck } from "lucide-react";

const ESTADO_CIVIL = ["solteiro", "casado", "divorciado", "viúvo", "união estável"];

// Documentos obrigatórios para upload no cadastro de cliente
const CLIENT_DOC_TYPES = [
  "documento_frente",
  "documento_verso",
  "selfie_documento",
  "comprovante_residencia",
  "foto_fachada",
];

function Field({ label, children, required, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="fc-label">{label}{required && <span className="text-[#D4AF37] ml-0.5">*</span>}</label>
      {children}
    </div>
  );
}

function DocUpload({ docType, file, onPick }) {
  return (
    <label
      className={`flex items-center gap-2.5 p-2.5 rounded-md border cursor-pointer transition-colors col-span-2 sm:col-span-1 ${file ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : "border-[#262626] hover:border-[#3a3a3a] bg-[#0f0f0f]"}`}
      data-testid={`form-doc-${docType}`}
    >
      <input
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onPick(docType, e.target.files?.[0])}
      />
      <div className="w-8 h-8 rounded-md bg-[#1A1A1A] border border-[#262626] flex items-center justify-center flex-shrink-0">
        {file ? <FileCheck className="text-[#D4AF37]" size={14}/> : <Upload className="text-[#A3A3A3]" size={14}/>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium leading-tight">{DOC_TYPE_LABELS[docType]}</div>
        <div className="text-[10.5px] text-[#A3A3A3] truncate leading-tight mt-0.5">{file ? file.name : "Toque para enviar"}</div>
      </div>
    </label>
  );
}

export default function ClientesPage() {
  const { user } = useAuth();
  const canCreate = user?.role === "admin" || user?.role === "gestora";
  const [items, setItems] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fIndicator, setFIndicator] = useState("");
  const [fSocio, setFSocio] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    full_name: "", cpf: "", rg: "", birth_date: "",
    phone: "", whatsapp: "", email: "",
    cep: "", address: "", city: "", state: "",
    profession: "", marital_status: "", client_notes: "",
    indicator_id: "", socio_id: "",
  });
  const [docs, setDocs] = useState({}); // doc_type -> File
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const pickDoc = (docType, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(`${DOC_TYPE_LABELS[docType]}: arquivo maior que 8MB`);
      return;
    }
    setDocs(prev => ({ ...prev, [docType]: file }));
  };

  const resetForm = () => {
    setForm({ full_name: "", cpf: "", rg: "", birth_date: "", phone: "", whatsapp: "", email: "", cep: "", address: "", city: "", state: "", profession: "", marital_status: "", client_notes: "", indicator_id: "", socio_id: "" });
    setDocs({});
    setUploadProgress({ done: 0, total: 0 });
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (fIndicator) params.indicator_id = fIndicator;
      if (fSocio) params.socio_id = fSocio;
      if (fStatus) params.status = fStatus;
      const { data } = await api.get("/clients-extended", { params });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.role !== "indicador" && user?.role !== "socio") {
      api.get("/indicators").then(r => setIndicators(r.data)).catch(()=>{});
      api.get("/socios").then(r => setSocios(r.data)).catch(()=>{});
    }
  }, [user]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, fIndicator, fSocio, fStatus]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      Object.keys(body).forEach(k => { if (body[k] === "") delete body[k]; });
      const { data: created } = await api.post("/clients", body);
      const clientId = created.id;

      // Upload documentos vinculados ao cliente recém-criado
      const docEntries = Object.entries(docs);
      if (docEntries.length > 0) {
        setUploadProgress({ done: 0, total: docEntries.length });
        for (let i = 0; i < docEntries.length; i++) {
          const [docType, file] = docEntries[i];
          const fd = new FormData();
          fd.append("document_type", docType);
          fd.append("file", file);
          try {
            await api.post(`/clients/${clientId}/documents`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (uerr) {
            toast.error(`Falha ao enviar ${DOC_TYPE_LABELS[docType]}`);
          }
          setUploadProgress({ done: i + 1, total: docEntries.length });
        }
      }

      toast.success(docEntries.length > 0 ? `Cliente cadastrado com ${docEntries.length} documento(s)` : "Cliente cadastrado");
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao cadastrar");
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Carteira</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">Clientes</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">{items.length} resultado(s)</p>
        </div>
        {canCreate && (
          <button onClick={()=>setOpen(true)} className="fc-btn-gold flex items-center gap-2" data-testid="btn-new-client">
            <Plus size={15}/> Novo cliente
          </button>
        )}
      </div>

      <div className="fc-card p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]"/>
          <input className="fc-input pl-9" placeholder="Buscar nome, CPF, WhatsApp..." value={search} onChange={e=>setSearch(e.target.value)} data-testid="clients-search"/>
        </div>
        <select className="fc-input min-w-[160px]" value={fStatus} onChange={e=>setFStatus(e.target.value)} data-testid="filter-status">
          <option value="">Todos status</option>
          {Object.entries(STATUS_LABELS).map(([v,l])=>(<option key={v} value={v}>{l}</option>))}
        </select>
        {(user?.role==="admin"||user?.role==="gestora") && (
          <select className="fc-input min-w-[160px]" value={fIndicator} onChange={e=>setFIndicator(e.target.value)} data-testid="filter-indicator">
            <option value="">Indicadores</option>
            {indicators.map(i=>(<option key={i.id} value={i.id}>{i.name}</option>))}
          </select>
        )}
        {(user?.role==="admin"||user?.role==="gestora") && (
          <select className="fc-input min-w-[160px]" value={fSocio} onChange={e=>setFSocio(e.target.value)} data-testid="filter-socio">
            <option value="">Sócios</option>
            {socios.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
        )}
      </div>

      <div className="fc-card overflow-hidden">
        {/* Tabela desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#A3A3A3] border-b border-[#1f1f1f]">
                <th className="py-3.5 px-4">Cliente</th>
                <th className="py-3.5 px-4">CPF</th>
                <th className="py-3.5 px-4">WhatsApp</th>
                <th className="py-3.5 px-4">Cidade</th>
                <th className="py-3.5 px-4">Indicador</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4 text-right pr-6">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && [1,2,3].map(i=>(<tr key={i} className="border-b border-[#1f1f1f]"><td colSpan={7} className="py-3"><div className="h-5 shimmer rounded"/></td></tr>))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-[#737373]">Nenhum cliente.</td></tr>
              )}
              {!loading && items.map(c => (
                <tr key={c.id} className="border-b border-[#1f1f1f] hover:bg-[#121212] transition-colors" data-testid={`client-row-${c.id}`}>
                  <td className="py-3.5 px-4 font-medium">{c.full_name}</td>
                  <td className="py-3.5 px-4 text-[#A3A3A3] font-mono-tab">{c.cpf_masked || maskCPF(c.cpf)}</td>
                  <td className="py-3.5 px-4 text-[#A3A3A3]">{maskPhone(c.whatsapp)}</td>
                  <td className="py-3.5 px-4 text-[#A3A3A3]">{c.city || "—"}/{c.state || "—"}</td>
                  <td className="py-3.5 px-4 text-[#A3A3A3]">{c.indicator_name || "—"}</td>
                  <td className="py-3.5 px-4"><StatusBadge status={c.status}/></td>
                  <td className="py-3.5 px-4 text-right pr-6">
                    <Link to={`/clientes/${c.id}`} className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:underline text-[13px]">
                      Abrir <ArrowRight size={13}/>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards mobile */}
        <div className="md:hidden divide-y divide-[#1f1f1f]" data-testid="clients-cards">
          {loading && [1,2,3].map(i=>(<div key={i} className="p-4"><div className="h-16 shimmer rounded"/></div>))}
          {!loading && items.length === 0 && (
            <div className="py-12 text-center text-[#737373] text-sm">Nenhum cliente.</div>
          )}
          {!loading && items.map(c => (
            <Link
              key={c.id}
              to={`/clientes/${c.id}`}
              className="flex items-start justify-between gap-3 p-4 active:bg-[#141414] transition-colors"
              data-testid={`client-card-${c.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[14.5px] truncate">{c.full_name}</div>
                <div className="text-[12px] text-[#A3A3A3] font-mono-tab mt-0.5">{c.cpf_masked || maskCPF(c.cpf)}</div>
                <div className="text-[12px] text-[#A3A3A3] mt-0.5 truncate">{maskPhone(c.whatsapp)} · {c.city || "—"}/{c.state || "—"}</div>
                <div className="text-[11.5px] text-[#737373] mt-0.5 truncate">{c.indicator_name || "Sem indicador"}</div>
                <div className="mt-2"><StatusBadge status={c.status}/></div>
              </div>
              <ArrowRight size={16} className="text-[#D4AF37] flex-shrink-0 mt-1"/>
            </Link>
          ))}
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-stretch sm:items-center justify-center sm:p-4 overflow-y-auto"
          onClick={() => { if (!saving) { setOpen(false); resetForm(); } }}
        >
          <form
            onSubmit={create}
            className="fc-card w-full sm:max-w-3xl sm:my-8 flex flex-col sm:max-h-[92vh] min-h-screen sm:min-h-0 rounded-none sm:rounded-md"
            onClick={e=>e.stopPropagation()}
            data-testid="client-create-modal"
          >
            {/* Header sticky */}
            <div className="flex items-start justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-[#1f1f1f] flex-shrink-0">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Cadastro interno</div>
                <h3 className="font-display text-xl sm:text-2xl font-bold mt-0.5">Novo cliente</h3>
              </div>
              <button
                type="button"
                onClick={() => { if (!saving) { setOpen(false); resetForm(); } }}
                className="text-[#737373] hover:text-white p-1"
                data-testid="form-close"
              >
                <X size={18}/>
              </button>
            </div>

            {/* Body scrollable */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5 space-y-5">
              {/* Dados pessoais */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] mb-2.5">Dados pessoais</div>
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                  <Field label="Nome completo" required full>
                    <input required className="fc-input" value={form.full_name} onChange={e=>setForm({...form, full_name: e.target.value})} data-testid="form-full_name"/>
                  </Field>
                  <Field label="CPF" required>
                    <input required className="fc-input" inputMode="numeric" value={form.cpf} onChange={e=>setForm({...form, cpf: maskCPF(e.target.value)})} data-testid="form-cpf"/>
                  </Field>
                  <Field label="RG">
                    <input className="fc-input" value={form.rg} onChange={e=>setForm({...form, rg: e.target.value})} data-testid="form-rg"/>
                  </Field>
                  <Field label="Nascimento">
                    <input type="date" className="fc-input" value={form.birth_date} onChange={e=>setForm({...form, birth_date: e.target.value})} data-testid="form-birth"/>
                  </Field>
                  <Field label="Estado civil">
                    <select className="fc-input" value={form.marital_status} onChange={e=>setForm({...form, marital_status: e.target.value})} data-testid="form-marital">
                      <option value="">—</option>
                      {ESTADO_CIVIL.map(s=>(<option key={s} value={s}>{s}</option>))}
                    </select>
                  </Field>
                  <Field label="Telefone">
                    <input className="fc-input" inputMode="tel" value={form.phone} onChange={e=>setForm({...form, phone: maskPhone(e.target.value)})} data-testid="form-phone"/>
                  </Field>
                  <Field label="WhatsApp" required>
                    <input required className="fc-input" inputMode="tel" value={form.whatsapp} onChange={e=>setForm({...form, whatsapp: maskPhone(e.target.value)})} data-testid="form-whatsapp"/>
                  </Field>
                  <Field label="E-mail" full>
                    <input type="email" className="fc-input" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} data-testid="form-email"/>
                  </Field>
                </div>
              </div>

              {/* Endereço */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] mb-2.5">Endereço e profissão</div>
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                  <Field label="CEP">
                    <input className="fc-input" inputMode="numeric" value={form.cep} onChange={e=>setForm({...form, cep: e.target.value})} data-testid="form-cep"/>
                  </Field>
                  <Field label="Profissão">
                    <input className="fc-input" value={form.profession} onChange={e=>setForm({...form, profession: e.target.value})} data-testid="form-profession"/>
                  </Field>
                  <Field label="Cidade">
                    <input className="fc-input" value={form.city} onChange={e=>setForm({...form, city: e.target.value})} data-testid="form-city"/>
                  </Field>
                  <Field label="UF">
                    <input maxLength={2} className="fc-input uppercase" value={form.state} onChange={e=>setForm({...form, state: e.target.value.toUpperCase()})} data-testid="form-state"/>
                  </Field>
                  <Field label="Endereço completo" full>
                    <textarea rows={2} className="fc-input resize-none" value={form.address} onChange={e=>setForm({...form, address: e.target.value})} data-testid="form-address"/>
                  </Field>
                </div>
              </div>

              {/* Vínculos */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] mb-2.5">Vínculos</div>
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                  <Field label="Indicador responsável">
                    <select className="fc-input" value={form.indicator_id} onChange={e=>setForm({...form, indicator_id: e.target.value})} data-testid="form-indicator">
                      <option value="">—</option>
                      {indicators.map(i=>(<option key={i.id} value={i.id}>{i.name}</option>))}
                    </select>
                  </Field>
                  <Field label="Sócio responsável">
                    <select className="fc-input" value={form.socio_id} onChange={e=>setForm({...form, socio_id: e.target.value})} data-testid="form-socio">
                      <option value="">—</option>
                      {socios.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
                    </select>
                  </Field>
                </div>
              </div>

              {/* Documentos */}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] mb-2.5">
                  Documentos do cliente
                  <span className="text-[#737373] normal-case tracking-normal ml-2">Imagens ou PDF, até 8MB</span>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {CLIENT_DOC_TYPES.map(dt => (
                    <DocUpload key={dt} docType={dt} file={docs[dt]} onPick={pickDoc}/>
                  ))}
                </div>
              </div>

              {/* Observações */}
              <div>
                <Field label="Observações" full>
                  <textarea rows={3} className="fc-input resize-none" value={form.client_notes} onChange={e=>setForm({...form, client_notes: e.target.value})} data-testid="form-notes"/>
                </Field>
              </div>
            </div>

            {/* Footer sticky */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 sm:px-5 sm:py-4 border-t border-[#1f1f1f] flex-shrink-0 bg-[#0d0d0d]">
              <button
                type="button"
                onClick={() => { if (!saving) { setOpen(false); resetForm(); } }}
                disabled={saving}
                className="fc-btn-ghost text-sm px-4 py-2 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="fc-btn-gold flex items-center gap-2 text-sm px-4 py-2"
                data-testid="form-submit"
              >
                {saving && <Loader2 size={14} className="animate-spin"/>}
                {saving && uploadProgress.total > 0
                  ? `Enviando ${uploadProgress.done}/${uploadProgress.total}`
                  : (saving ? "Cadastrando..." : "Cadastrar")}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
